'use client';

import React, { useRef, useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function BecomeHostBanner() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-10 px-4 sm:px-6 max-w-7xl mx-auto">
      <div
        className={`relative rounded-3xl overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Background */}
        <div className="relative h-80 md:h-96">
          <AppImage
            src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1400&q=80"
            alt="Beautiful property with pool and tropical garden perfect for hosting guests"
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        </div>

        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div className="max-w-lg">
            <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-3">For Property Owners</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              Turn your space into<br />extra income
            </h2>
            <p className="text-white/80 text-base mb-6 leading-relaxed">
              Join thousands of hosts on Ndoto. List your property for free and start earning from guests across Africa and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/host"
                className="bg-white text-gray-900 font-bold px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors text-sm text-center"
              >
                Become a Host
              </a>
              <a
                href="/host/learn-more"
                className="border border-white text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors text-sm text-center"
              >
                Learn more
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-8">
              {[
                { value: 'UGX 0', label: 'Listing fee' },
                { value: '14,000+', label: 'Active listings' },
                { value: '200+', label: 'Cities covered' },
              ]?.map((stat) => (
                <div key={stat?.label}>
                  <p className="text-white font-bold text-lg">{stat?.value}</p>
                  <p className="text-white/70 text-xs">{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
